import React from "react";
import GroupAddress from "../models/GroupAdress";
import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";
import Logger from "../Logger";
import ConfirmDialog from "../modelDialogs/ConfirmDialog";
import UndoStep from "../undomanager/UndoStep";
import { LocalEtsAppUrl, etsFetch } from "../utils/axios";
import BaseModel from "../models/BaseModel";
import Actor from "../models/Actor";
import Consumer from "../models/Consumer";
import BaseModelReference from "../models/BaseModelReference";
import { isUiModeExpert } from "../uiMode";
import { itx, noItx } from "../translationItx/itx";

import Signal from "../models/Signal";
import Channel from "../models/Channel";
import TreeNodeInfo from "../models/TreeNodeInfo";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { isDevelopmentEnvironment } from "../estada-config";
import TooltipTarget from "../components/tooltip/TooltipTarget";
import ObjectReference from "../components/ObjectReference";
import { EventKey } from "../eventLogger/EventKeys";
import { closeLocalEtsWebSocket, localEtsWebSocket, openLocalEtsWebSocket } from "../etsFunctions/etsWebSocket";

const logger = new Logger();

async function readGroupAddressValueViaLocalWs(grpAddress: string): Promise<any | undefined> {
  try {
    openLocalEtsWebSocket();
    await localEtsWebSocket!.waitForOpen(2000);
    return await localEtsWebSocket!.sendCommandAsync("communication/read", { address: grpAddress }, 5000); // no itx
  } catch (ex) {
    logger.error(ex);
    if (localEtsWebSocket?.isClosed) closeLocalEtsWebSocket();
    return undefined;
  }
}

async function readGroupAddressValueViaHttpFallback(grpAddress: string): Promise<any | undefined> {
  const response = await etsFetch(`${LocalEtsAppUrl}/communication/read`, {
    // no itx
    method: "POST", // no itx
    body: JSON.stringify({ address: grpAddress }),
  });
  if (!response?.ok) return undefined;
  return await response.json();
}

function OnlineData(props: { groupAddress: GroupAddress }) {
  const { groupAddress } = props;
  useOnChangeWatcher(groupAddress);

  let backgroundColor = "lightgrey"; //

  let tooltipHtml = itx("GroupAddressTreeviewModel.noValueYet", "No Value yet received");
  if (groupAddress.receivedTimeStamp) {
    const sTime = groupAddress.receivedTimeStamp.toLocaleString(navigator.language, {
      hour: "2-digit", // no itx
      minute: "2-digit", // no itx
      second: "2-digit", // no itx
    });

    tooltipHtml = `Last value received at ${sTime} <br> ${groupAddress.receivedCount} values received`;

    backgroundColor = "lightgreen"; //
  } else {
    backgroundColor = "var(--warning-color)";
  }

  if (groupAddress.sendTimeStamp) {
    const sTime = groupAddress.sendTimeStamp.toLocaleString(navigator.language, {
      hour: "2-digit", // no itx
      minute: "2-digit", // no itx
      second: "2-digit", // no itx
    });

    tooltipHtml = `Last value debug send at ${sTime}`;

    backgroundColor = "lightblue"; //
  }

  return (
    <div
      className="nav_C1"
      style={{
        backgroundColor: backgroundColor,
        borderRadius: "20px",
        height: "25px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginLeft: "10px",
        marginRight: "10px",
        padding: "5px",
        paddingLeft: "10px",
        paddingRight: "10px",
        cursor: "default",
      }}
      data-tooltip-id="tv-tooltip"
      data-tooltip-html={tooltipHtml}
      data-tooltip-place="right"
      data-tooltip-variant="warning" // no itx
      data-noitx="true" // no itx
      suppressContentEditableWarning={true}
      contentEditable={true}
      draggable="false" // no itx
      tabIndex={1}
      onPointerDown={(ev: any) => {
        if (ev.target.innerHTML == "-/-") {
          ev.target.innerHTML = "";
        }
      }}
      onFocus={(ev: any) => {
        // if (ev.target.innerText == "-/-") {
        //   if (window.getSelection && document.createRange) {
        //     const range = document.createRange();
        //     range.selectNodeContents(ev.target);
        //     const sel = window.getSelection();
        //     sel?.removeAllRanges();
        //     sel?.addRange(range);
        //   }
        //   ev.target.innerText == ""
        // }
      }}
      onBlur={(ev: any) => {
        const v = ev.target.innerText.trim();
        groupAddress.sendValueToKnxBridge(v);
      }}
      onKeyUp={(ev: any) => {
        if (ev.key == "Enter") {
          const v = ev.target.innerText.trim();
          groupAddress.sendValueToKnxBridge(v);
        }
      }}
    >
      {groupAddress.currentValue || "-/-"}
    </div>
  );
}

export default class GroupAddressTreeViewModel extends TreeViewModel {
  get typedObj(): GroupAddress {
    return this.obj as GroupAddress;
  }

  get isFolder(): boolean {
    if (this.typedObj.level <= 2) {
      return true;
    }

    if (isUiModeExpert()) {
      return true;
    }

    return true;
  }

  get iconUrl(): string | undefined {
    if (this.typedObj) {
      switch (this.typedObj.level) {
        case 3:
          return "las la-border-all groupAddress3"; // no itx

        case 2:
          return "las la-border-all groupAddress2"; // no itx

        case 1:
          return "las la-border-all groupAddress1"; // no itx
      }
    }

    return undefined;
  }

  getTagNeeded(): boolean {
    return true;
  }

  getDisableEdit(): boolean {
    //
    // nur manuelle GruppenAddressen können editiert werden
    //
    const ga = this.typedObj as GroupAddress;
    return !ga.isManualGroupAddress;
  }

  getFullTitle(isExpanded: boolean): string | undefined | JSX.Element {
    //
    // Erlaube "normales" Editieren nur bei manuellen GruppenAddressen
    //
    const ga = this.typedObj as GroupAddress;
    if (ga.selected && ga.isManualGroupAddress) {
      return undefined;
    }

    if (this.typedObj.level == 3) {
      const ss = this.typedObj.displayName.split("-");
      if (ss[2]) {
        ss[1] += " - " + ss[2];
      }

      //
      // split to creacte colums
      //
      return (
        <div style={{ display: "flex", flexDirection: "row" }}>
          {state.installationMode && <OnlineData groupAddress={this.typedObj} />}

          {ss.map((s, index) => (
            <div key={index} className={`nav_C${index + 2}`}>
              {s}
              {index < ss.length - 1 && <>&nbsp;-&nbsp;</>}
            </div>
          ))}
        </div>
      );
    } else {
      return <div className="nav_C2">{this.typedObj.displayName}</div>;
    }
  }

  async readGroupAddressValue(grpAddress: string) {
    if (!this.typedObj.currentValue) {
      this.typedObj.currentValue = itx("homePage.loading", "Loading...");
    }
    try {
      const responseData =
        (await readGroupAddressValueViaLocalWs(grpAddress)) ?? (await readGroupAddressValueViaHttpFallback(grpAddress));
      if (responseData?.status == "OK" && responseData?.body) {
        this.typedObj.currentValue = responseData.body.groupValue;
      } else {
        this.typedObj.currentValue = "N/A"; // no itx
      }
      this.typedObj.raiseOnChange();
      return true;
    } catch (ex) {
      this.typedObj.currentValue = "N/A"; // no itx
      this.typedObj.raiseOnChange();
      return false;
    }
  }

  getChildCollectionEx(parentTreeNodeInfo: TreeNodeInfo): Array<TreeNodeInfo> | undefined {
    let res = new Array<TreeNodeInfo>();

    if (this.typedObj.level <= 2) {
      this.typedObj.children?.sortIntBy("address").forEach((a) => res.push(new TreeNodeInfo(parentTreeNodeInfo, a)));
    } else {
      //
      // Auf Level 3 haben wir die tatsächlichen GruppenAdressen
      //

      //
      // unter diesen stellen wir alle Verwendungen der GruppenAdresse dar
      //

      const signals = this.typedObj.getSignalBackpointers(false).sort((a, b) => {
        if (a.className != b.className) {
          return a.className.localeCompare(b.className);
        }
        return a.fullTag.localeCompare(b.fullTag);
      });

      // alle Signale die diese GruppenAdresse referenzieren,
      //    es sein denn das Signal ist selbst der Owner im Navigator
      //   (Verhinderung von Endlosrekursionen im Navigator)
      //
      signals.forEach((signal) => {
        const signalParent = signal.parent as BaseModel;

        const refSignal = new BaseModelReference({ referencedObject: signal }, this.typedObj);
        if (!parentTreeNodeInfo.isChildOf(signalParent)) {
          refSignal.overwriteIcon = signalParent?.treeViewModel.icon;

          if (refSignal.referencedObject?.className == "Signal") {
            const signal = refSignal.referencedObject as Signal;

            if (true) {
              // signalParent?.isChannel) { // } || signalParent?.isConsumer || signalParent?.isButtonChannel) {
              const channel = signalParent as Channel;

              if (this.typedObj.isManualGroupAddress) {
                refSignal.onDisconnect = () => {
                  const ga = this.typedObj;

                  signal.groupAddresses.remove(ga);
                  ga.removeSignalBackpointer(signal);

                  signal.raiseOnChangeUI("childCollection");
                  signal.raiseOnChange("groupAddresses");
                  ga.raiseOnChange("signalBackpointer");
                  ga.raiseOnChangeUI("childCollection");

                  if (ga.getSignalBackpointers().length === 0) {
                    state.dialogManager.openDialog(
                      <ConfirmDialog
                        title={itx("KnxComObjectTable.emptyGroupAdress", "Gruppenadresse ohne Verwendungen")}
                        text={itx(
                          "KnxComObjectTable.deleteGroupAdress",
                          "Soll die nicht mehr verwendete Gruppenadresse gelöscht werden?",
                        )}
                        onOk={() => {
                          ga.delete();
                          ga.parent?.raiseOnChange("childCollection");
                          return true;
                        }}
                      />,
                    );
                  }

                  return true;
                };
              }

              if ((signal.parent as BaseModel)?.isConsumer) {
                refSignal.overwriteFulltag = signal.parent?.fullTag || "";
              } else if ((signal.parent as BaseModel)?.isMultiplexer) {
                refSignal.overwriteFulltag = signal.parent?.fullTag || "";
              } else {
                refSignal.overwriteFulltag = signal.fullTag;
              }

              refSignal.overwriteFullTitle = (
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <div className="nav_C3">{signal.knxFunctionText}</div>
                  <div className="nav_C4">
                    &nbsp;
                    {channel.nameOfDefaultName}
                  </div>
                </div>
              );
            }
          }

          const refInfo = new TreeNodeInfo(parentTreeNodeInfo, refSignal);
          console.log("refInfo:", refInfo, refSignal);
          res.push(refInfo);
        }
      });
    }

    return res;
  }

  getTitlePostfix(): string | JSX.Element {
    if (this.typedObj.level == 3) {
      if (state.installationMode) {
        return "";
      }

      const signals = this.typedObj.getSignalBackpointers();

      const referencesWithoutConsumerAndMultiplexer = signals.filter((signal) => {
        const p = signal?.parent as BaseModel;
        if (p) {
          return !p.isConsumer && !p.isMultiplexer;
        } else {
          console.error("Signal without parent?", signal);
          return false;
        }
      });

      const l = referencesWithoutConsumerAndMultiplexer.length;

      const actorChannel = signals.find((r) => r?.parent?.className == "Channel");

      const isReferncedByActor = actorChannel != undefined;

      const isReferncedByButton = signals.find((r) => (r?.parent as BaseModel)?.isButtonChannel);

      const consumer = signals.find((r) => (r?.parent as BaseModel)?.isConsumer)?.parent as Consumer;

      let tooltipText = "";

      let backgroundColor: string | undefined;

      if (l == 0) {
        tooltipText = itx("groupAdressTreeViewModel.groupAdressNotUsed", "Diese Gruppenadesse wird nicht verwendet");
        backgroundColor = "var(--error-color)";
      } else {
        if (!isReferncedByActor) {
          if (tooltipText != "") {
            tooltipText += "\n";
          }

          backgroundColor = "var(--warning-color)"; // no itx
          tooltipText += itx("groupAdressTreeViewModel.noConnectionWithActor", "Kein Aktor verbunden");

          const channel1 = consumer?.getConnectedChannel();

          if (channel1 && (channel1.parent as Actor)?.template == undefined) {
            tooltipText += ",\n" + `da dem Aktor ${channel1.parent?.fullTag} kein KNX Produkt zugewiesen ist.`;
          } else {
            tooltipText += ".";
          }
        }

        if (!isReferncedByButton && !this.typedObj.isStatus) {
          if (tooltipText != "") {
            tooltipText += "\n";
          }

          backgroundColor = "var(--warning-color)"; // no itx
          tooltipText += itx("groupAdressTreeViewModel.noConnectionWithButton", "Kein Taster verbunden.");
        }
      }

      let tooltipHtml = "";
      if (tooltipText) {
        tooltipHtml = tooltipText.replaceAll("\n", "<br />");
      }

      const style = {
        marginLeft: "10px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center",
        backgroundColor: backgroundColor,
        border: "1px solid grey",
        borderRadius: "20px",
        width: "20px",
        height: "20px",
      };

      let groupAddressSourceTooltipText = `Diese Gruppenadresse wurde erzeugt von:`;

      const generatorObject = this.typedObj.generatorObject;
      const generatorObjectText = generatorObject
        ? generatorObject.fullTag + " " + generatorObject.name
        : itx("GroupAddressTreeviewModel.unknown", "-unbekannt-");

      groupAddressSourceTooltipText = groupAddressSourceTooltipText.replaceAll("T1", generatorObjectText);

      return (
        <div className="estadaRow">
          <TooltipTarget
            tooltip={() => (
              <div className="tooltip_V2">
                <div className="estadaColumn left">
                  <div className="estadaRow">
                    {itx("GroupAddressTreeviewModel.createdBy", "Erzeugt von:")}
                    {generatorObject && <ObjectReference obj={generatorObject} />}
                  </div>
                  {tooltipText && (
                    <div
                      className="estadaRow"
                      style={{
                        backgroundColor: backgroundColor,
                        marginTop: "5px",
                        padding: "5px",
                        borderRadius: "5px",
                      }}
                    >
                      <i className="las la-exclamation-triangle"></i> &nbsp; {tooltipText}
                    </div>
                  )}
                </div>
              </div>
            )}
          >
            <div style={style}>{l}</div>
          </TooltipTarget>
        </div>
      );
    } else if (isDevelopmentEnvironment() && this.typedObj.level == 1) {
      let count = 0;

      for (const a2 of this.typedObj.children.asArray()) {
        count += a2.children.size;
      }
      return <div className="nav_C4">({count})</div>;
    } else if (isDevelopmentEnvironment() && this.typedObj.level == 2) {
      let count = this.typedObj.children.size;
      return <div className="nav_C4">({count})</div>;
    } else {
      return "";
    }
  }

  getTagStyle(): any | undefined {
    if (this.typedObj.isManualGroupAddress) {
      return { borderRadius: "20px", backgroundColor: "lightblue", paddingLeft: "10px", paddingRight: "10px" };
    } else {
      return undefined;
    }
  }
  getDisableEditTag(): boolean {
    return !this.typedObj.isManualGroupAddress;
  }

  allowDrag(): boolean {
    //allow only dragging of last group adress ( level 3)

    return this.typedObj?.level == 3;

    const ga = this.typedObj as GroupAddress;
    return ga.isManualGroupAddress || ga.isFixedGroupAddress;
  }

  allowDrop(): boolean {
    return true;

    const ga = this.typedObj as GroupAddress;
    return ga.isManualGroupAddress || ga.isFixedGroupAddress;
  }

  handleAdd() {
    const groupAddress = this.typedObj as GroupAddress;
    if (groupAddress.isManualGroupAddressFolder) {
      // get free adress
      let freeAddress = 1;
      for (const a of groupAddress.children.asArray()) {
        if (Number(a.address) >= freeAddress) {
          freeAddress = Number(a.address) + 1;
        }
      }
      const newGroupAddress = GroupAddress.createNewGroupAddress({
        parent: groupAddress,
        level: 3,
        name: itx("GroupAddressTreeviewModel.newGroupAddress", "neue Gruppenadresse"),
        address: freeAddress,
      });

      this.typedObj.children.add(newGroupAddress);
      state.selectionManager.selectedObject = newGroupAddress;
      state.eventLogger.addEvent(EventKey.GroupAddressManuallyCreated, {
        gaId: newGroupAddress.id,
        parentId: groupAddress.id,
      });
    }
  }

  getButtons(): JSX.Element[] | undefined {
    const groupAddress = this.typedObj as GroupAddress;
    if (groupAddress.isManualGroupAddressFolder) {
      return [this.inlineButton_Add("inlineButton.addGroupAddress")]; // no itx
    }
    return undefined;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    try {
      const groupAddress = this.typedObj as GroupAddress;

      if (mode == DragMode.copy) {
        await state.menubarActions.onCopy(obj);
        await state.menubarActions.onPaste(this.typedObj);
        return new DropResult(true);
      }

      switch (obj.className) {
        case noItx("Signal"):
          let signal = obj as Signal;

          const res = signal.connectWith(groupAddress);
          if (res) {
            return new DropResult(true);
          }
      }
    } catch (ex) {}

    return new DropResult(false);
  }
}
